/* Hallmark · pre-emit critique: P5 H5 E5 S5 R5 V5 */
import React, { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { api } from '../utils/api'
import { useDcStore } from '../stores/dcStore'
import { useAuthStore } from '../stores/authStore'
import { canManageDc } from '../stores/authRoles.logic'
import { useConfirmStore } from '../store/confirmStore'
import { pathAfterCreateDc } from '../utils/dcContext.logic'
import { rackLabel } from '../utils/rackName.logic'
import toast from 'react-hot-toast'
import {
  Layers, Cpu, Plus, Trash2, Loader2, Zap,
  CheckCircle2, AlertTriangle, XCircle, ArrowRight,
  Settings, Server, Network, Info, LayoutGrid
} from 'lucide-react'

interface HealthResponse {
  status: string
  components: Record<string, string>
  version: string
}

/* ---------------------------------------------------------------------- */
/*  Sub-components                                                        */
/* ---------------------------------------------------------------------- */

function ProgressRing({
  value,
  size = 72,
  stroke = 6,
  colorClass = 'text-accent'
}: {
  value: number
  size?: number
  stroke?: number
  colorClass?: string
}) {
  const radius = (size - stroke) / 2
  const circumference = radius * 2 * Math.PI
  const offset = circumference - (Math.min(100, Math.max(0, value)) / 100) * circumference

  return (
    <div className="relative flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="transform -rotate-90">
        {/* Background circle */}
        <circle
          className="text-slate-200/20"
          strokeWidth={stroke}
          stroke="currentColor"
          fill="transparent"
          r={radius}
          cx={size / 2}
          cy={size / 2}
        />
        {/* Foreground circle */}
        <circle
          className={`${colorClass} transition-all duration-700 ease-out`}
          strokeWidth={stroke}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          stroke="currentColor"
          fill="transparent"
          r={radius}
          cx={size / 2}
          cy={size / 2}
        />
      </svg>
      <span className="absolute text-xs font-mono font-bold text-text-primary">
        {Math.round(value)}%
      </span>
    </div>
  )
}

function KpiCard({
  label,
  value,
  sub,
  icon: Icon,
  badgeColor = 'bg-accent/10 text-accent',
  to,
  children
}: {
  label: string
  value: string | number
  sub?: string
  icon?: any
  badgeColor?: string
  to?: string
  children?: React.ReactNode
}) {
  const content = (
    <>
      {/* Subtle glow effect */}
      <div className="absolute inset-0 bg-gradient-to-tr from-accent/10 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />

      <div className="z-10 space-y-1">
        <div className="text-text-secondary text-xs font-semibold uppercase tracking-wider">{label}</div>
        <div className="text-3xl font-bold tracking-tight text-text-primary font-mono">{value}</div>
        {sub && <div className="text-text-muted text-xs font-medium">{sub}</div>}
      </div>

      <div className="z-10 flex items-center">
        {children ? (
          children
        ) : (
          Icon && (
            <div className={`p-3 rounded-full ${badgeColor} transition-transform duration-300 group-hover:scale-110`}>
              <Icon className="w-6 h-6" />
            </div>
          )
        )}
      </div>
    </>
  )

  const className = "relative group bg-gradient-to-tr from-slate-900/60 to-slate-900/20 backdrop-blur-md border border-slate-800/40 hover:border-accent/60 rounded-sm p-5 hover:shadow-md transition-all duration-300 flex items-center justify-between overflow-hidden cursor-pointer"

  if (to) {
    return (
      <Link to={to} className={className}>
        {content}
      </Link>
    )
  }

  return (
    <div className={className.replace(" cursor-pointer", "")}>
      {content}
    </div>
  )
}

function ServiceStatus({ name, status }: { name: string; status: string }) {
  const isOk = status === 'ok'
  const isError = status === 'error'
  
  // Russian localization for service names
  const friendlyName =
    name === 'database_core'
      ? 'СУБД PostgreSQL'
      : name === 'timescaledb_extension'
      ? 'Расширение TimescaleDB'
      : name === 'migrations_uptodate'
      ? 'Миграции БД'
      : name

  return (
    <div className="flex items-center justify-between py-3 border-b border-slate-800/30 last:border-0 hover:bg-slate-800/25 px-2 transition-colors">
      <div className="flex items-center gap-2.5">
        {isOk ? (
          <CheckCircle2 className="w-4 h-4 text-status-normal" />
        ) : isError ? (
          <XCircle className="w-4 h-4 text-status-critical" />
        ) : (
          <AlertTriangle className="w-4 h-4 text-status-warning animate-pulse" />
        )}
        <span className="text-sm font-medium text-text-primary">{friendlyName}</span>
      </div>
      <span className={`text-xs font-mono px-2 py-0.5 rounded-full ${
        isOk ? 'bg-status-normal/10 text-status-normal border border-status-normal/20' :
        isError ? 'bg-status-critical/10 text-status-critical border border-status-critical/20' :
        'bg-status-warning/10 text-status-warning border border-status-warning/20'
      }`}>
        {status.toUpperCase()}
      </span>
    </div>
  )
}

function ActionBlock({
  to,
  title,
  desc,
  icon: Icon,
  colorClass = 'text-accent'
}: {
  to: string
  title: string
  desc: string
  icon: any
  colorClass?: string
}) {
  return (
    <Link
      to={to}
      className="group flex gap-4 p-4 rounded-sm bg-slate-900/10 hover:bg-slate-900/30 border border-slate-800/30 hover:border-accent/40 hover:shadow-sm transition-all duration-300 text-left"
    >
      <div className={`p-3 rounded-sm bg-slate-800/40 border border-slate-700/30 ${colorClass} group-hover:scale-105 transition-transform duration-300 self-start`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="space-y-1 flex-1">
        <h4 className="text-sm font-semibold text-text-primary group-hover:text-accent transition-colors flex items-center gap-1.5">
          {title}
          <ArrowRight className="w-3.5 h-3.5 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300" />
        </h4>
        <p className="text-xs text-text-secondary leading-relaxed">{desc}</p>
      </div>
    </Link>
  )
}

/* ---------------------------------------------------------------------- */
/*  Main View                                                             */
/* ---------------------------------------------------------------------- */

export default function Dashboard() {
  const [stats, setStats] = useState<any>(null)
  const [racks, setRacks] = useState<any[]>([])
  const [health, setHealth] = useState<HealthResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showAddDc, setShowAddDc] = useState(false)
  const [dcForm, setDcForm] = useState({
    code: '',
    name: '',
    rows_count: 5,
    max_racks_per_row: 20,
    rack_step_m: 0.6,
    aisle_width_m: 1.2,
    rack_depth_m: 1.2,
    rack_height_m: 2.2,
    tray_above_rack_m: 0.3,
    cable_slack_m: 1.5
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  const { dcCode } = useParams<{ dcCode: string }>()
  const { datacenters: meta, loading: storeLoading, fetchDatacenters, setActiveDcCode } = useDcStore()
  const canManage = canManageDc(useAuthStore(s => s.userRole))

  useEffect(() => {
    api.get('/api/health').then(r => setHealth(r.data)).catch(() => {})
  }, [])

  useEffect(() => {
    if (!dcCode) return
    setLoading(true)
    Promise.all([
      api.get(`/api/datacenters/${dcCode}/dashboard`),
      api.get(`/api/datacenters/${dcCode}/racks`)
    ])
      .then(([statsRes, racksRes]) => {
        setStats(statsRes.data)
        setRacks(racksRes.data)
        setLoading(false)
      })
      .catch(e => {
        setError(e.message)
        setLoading(false)
      })
  }, [dcCode])

  const handleCreateDc = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    try {
      await api.post('/api/datacenters', dcForm)
      setShowAddDc(false)
      setActiveDcCode(dcForm.code)
      await fetchDatacenters()
      window.location.href = pathAfterCreateDc(dcForm.code)
    } catch (err: any) {
      console.error(err)
      toast.error(err?.response?.data?.detail || err?.message || 'Не удалось создать ЦОД')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDeleteDc = async () => {
    if (!dcCode) return
    const confirmed = await useConfirmStore.getState().confirm({
      title: 'Удаление ЦОД',
      message: `Вы уверены, что хотите полностью удалить ЦОД "${dcCode}"? Это действие необратимо и удалит все стойки, размещенное оборудование, порты и проложенные кабельные линии.`,
      confirmText: 'Удалить',
      cancelText: 'Отмена'
    })
    if (!confirmed) return

    setIsDeleting(true)
    try {
      await api.delete(`/api/datacenters/${dcCode}`)
      localStorage.removeItem('activeDcCode')
      setActiveDcCode(null)
      await fetchDatacenters()
      window.location.href = '/'
    } catch (err: any) {
      console.error(err)
      toast.error(err?.response?.data?.detail || err?.message || 'Не удалось удалить ЦОД')
    } finally {
      setIsDeleting(false)
    }
  }

  if (storeLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-3 text-text-muted">
        <Loader2 className="w-6 h-6 animate-spin text-accent" />
        <div className="text-sm font-medium">Загрузка конфигурации ЦОД...</div>
      </div>
    )
  }

  if (meta.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8 text-center animate-fade-in max-w-xl mx-auto">
        <div className="p-4 bg-bg-panel/40 border border-border/30 rounded-full mb-6">
          <Layers className="w-12 h-12 text-accent" />
        </div>
        <h2 className="text-2xl font-bold tracking-tight text-text-primary mb-3">Добро пожаловать в DC Cable Link</h2>
        <p className="text-text-secondary text-sm mb-8 leading-relaxed">
          В системе пока нет зарегистрированных дата-центров.
          {canManage
            ? ' Создайте свой первый ЦОД, чтобы спроектировать зал, разместить монтажные стойки и спланировать кабельные трассы.'
            : ' Добавить ЦОД может менеджер.'}
        </p>
        {canManage && (
        <button
          onClick={() => setShowAddDc(true)}
          className="flex items-center gap-2 px-5 py-2.5 bg-accent text-white rounded hover:bg-accent-hover transition-colors font-medium shadow-md text-sm cursor-pointer"
        >
          <Plus className="w-4 h-4" />
          Создать первый ЦОД
        </button>
        )}

        {canManage && showAddDc && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex justify-center items-center z-[100] text-left">
            <div className="bg-bg-panel border border-border/60 rounded shadow-2xl p-6 w-[500px] animate-fade-in">
              <h3 className="text-lg font-bold mb-4 text-text-primary flex items-center gap-2">
                <Plus className="w-5 h-5 text-accent" />
                Новый Дата-центр
              </h3>
              <form onSubmit={handleCreateDc} className="space-y-4 text-sm">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="dc-code-input" className="block text-text-secondary font-medium mb-1.5">Код (латиница)</label>
                    <input id="dc-code-input" required type="text" className="w-full border border-border rounded px-3 py-2 bg-bg-input text-text-primary focus:outline-none focus:border-accent" value={dcForm.code} onChange={e => setDcForm({ ...dcForm, code: e.target.value })} />
                  </div>
                  <div>
                    <label htmlFor="dc-name-input" className="block text-text-secondary font-medium mb-1.5">Название</label>
                    <input id="dc-name-input" required type="text" className="w-full border border-border rounded px-3 py-2 bg-bg-input text-text-primary focus:outline-none focus:border-accent" value={dcForm.name} onChange={e => setDcForm({ ...dcForm, name: e.target.value })} />
                  </div>
                  <div>
                    <label htmlFor="dc-rows-input" className="block text-text-secondary font-medium mb-1.5">Кол-во рядов</label>
                    <input id="dc-rows-input" required type="number" className="w-full border border-border rounded px-3 py-2 bg-bg-input text-text-primary focus:outline-none focus:border-accent" value={dcForm.rows_count} onChange={e => setDcForm({ ...dcForm, rows_count: Number(e.target.value) })} />
                  </div>
                  <div>
                    <label htmlFor="dc-racks-input" className="block text-text-secondary font-medium mb-1.5">Стоек в ряду</label>
                    <input id="dc-racks-input" required type="number" className="w-full border border-border rounded px-3 py-2 bg-bg-input text-text-primary focus:outline-none focus:border-accent" value={dcForm.max_racks_per_row} onChange={e => setDcForm({ ...dcForm, max_racks_per_row: Number(e.target.value) })} />
                  </div>
                </div>
                <div className="flex justify-end gap-3 mt-6">
                  <button type="button" className="px-4 py-2 border border-border rounded hover:bg-bg-input transition-colors text-text-primary" onClick={() => setShowAddDc(false)}>Отмена</button>
                  <button type="submit" disabled={isSubmitting} className="px-4 py-2 bg-accent text-white rounded hover:bg-accent/90 transition-colors disabled:opacity-50 font-medium">Создать</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-3 text-text-muted">
        <Loader2 className="w-6 h-6 animate-spin text-accent" />
        <div className="text-sm font-medium">Загрузка данных дашборда...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-2 text-status-critical">
        <AlertTriangle className="w-8 h-8" />
        <div className="text-sm font-semibold">{error}</div>
      </div>
    )
  }

  const dc = meta.find(d => d.code === dcCode)

  const services = health?.components
    ? Object.entries(health.components).map(([name, status]) => ({ name, status }))
    : [{ name: 'Backend', status: 'unknown' }]

  // Generate DC Room Grid Map parameters
  const rows = dc?.rows_count || 5
  const cols = dc?.max_racks_per_row || 20

  return (
    <div className="p-6 space-y-6 animate-fade-in max-w-7xl mx-auto overflow-y-auto h-full pb-20">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-800/30 pb-5">
        <div className="space-y-1">
          <h1 className="text-2xl font-bold tracking-tight text-text-primary">Обзор платформы</h1>
          <p className="text-text-secondary text-sm">
            {dc ? `${dc.name} (${dc.code})` : 'ЦОД не выбран'} · DC Cable Link v{health?.version ?? '?'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {canManage && (
          <button
            onClick={handleDeleteDc}
            disabled={isDeleting}
            className="flex items-center gap-1.5 px-3 py-1.5 border border-status-critical/30 hover:border-status-critical/60 text-status-critical rounded text-xs font-semibold hover:bg-status-critical/10 hover:shadow-[0_0_8px_rgba(239,68,68,0.15)] transition-colors cursor-pointer"
          >
            {isDeleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
            Удалить ЦОД
          </button>
          )}
          {canManage && (
          <button
            onClick={() => setShowAddDc(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-accent text-white rounded text-xs font-semibold hover:bg-accent-hover transition-colors shadow-sm cursor-pointer"
          >
            <Plus className="w-3.5 h-3.5" />
            Добавить ЦОД
          </button>
          )}
          <div className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${
            health?.status === 'ok'
              ? 'bg-status-normal/10 text-status-normal border-status-normal/20'
              : 'bg-status-warning/10 text-status-warning border-status-warning/20'
          }`}>
            <span className={`w-1.5 h-1.5 rounded-full ${health?.status === 'ok' ? 'bg-status-normal' : 'bg-status-warning'} animate-pulse`} />
            {health?.status === 'ok' ? 'Система активна' : 'Статус неизвестен'}
          </div>
        </div>
      </div>

      {canManage && showAddDc && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex justify-center items-center z-[100]">
          <div className="bg-bg-panel border border-border/60 rounded shadow-2xl p-6 w-[500px] animate-fade-in">
            <h3 className="text-lg font-bold mb-4 text-text-primary flex items-center gap-2">
              <Plus className="w-5 h-5 text-accent" />
              Новый Дата-центр
            </h3>
            <form onSubmit={handleCreateDc} className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="dc-code-input" className="block text-text-secondary font-medium mb-1.5">Код (латиница)</label>
                  <input id="dc-code-input" required type="text" className="w-full border border-border rounded px-3 py-2 bg-bg-input text-text-primary focus:outline-none focus:border-accent" value={dcForm.code} onChange={e => setDcForm({ ...dcForm, code: e.target.value })} />
                </div>
                <div>
                  <label htmlFor="dc-name-input" className="block text-text-secondary font-medium mb-1.5">Название</label>
                  <input id="dc-name-input" required type="text" className="w-full border border-border rounded px-3 py-2 bg-bg-input text-text-primary focus:outline-none focus:border-accent" value={dcForm.name} onChange={e => setDcForm({ ...dcForm, name: e.target.value })} />
                </div>
                <div>
                  <label htmlFor="dc-rows-input" className="block text-text-secondary font-medium mb-1.5">Кол-во рядов</label>
                  <input id="dc-rows-input" required type="number" className="w-full border border-border rounded px-3 py-2 bg-bg-input text-text-primary focus:outline-none focus:border-accent" value={dcForm.rows_count} onChange={e => setDcForm({ ...dcForm, rows_count: Number(e.target.value) })} />
                </div>
                <div>
                  <label htmlFor="dc-racks-input" className="block text-text-secondary font-medium mb-1.5">Стоек в ряду</label>
                  <input id="dc-racks-input" required type="number" className="w-full border border-border rounded px-3 py-2 bg-bg-input text-text-primary focus:outline-none focus:border-accent" value={dcForm.max_racks_per_row} onChange={e => setDcForm({ ...dcForm, max_racks_per_row: Number(e.target.value) })} />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button type="button" className="px-4 py-2 border border-border rounded hover:bg-bg-input transition-colors text-text-primary" onClick={() => setShowAddDc(false)}>Отмена</button>
                <button type="submit" disabled={isSubmitting} className="px-4 py-2 bg-accent text-white rounded hover:bg-accent/90 transition-colors disabled:opacity-50 font-medium">Создать</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* KPI Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats && (
          <>
            <KpiCard label="Стойки" value={stats.total_racks} icon={Layers} badgeColor="bg-blue-500/10 text-blue-400" sub="Стойки на плане зала" to={`/${dcCode}/racks`} />
            <KpiCard label="Оборудование" value={stats.total_servers} icon={Cpu} badgeColor="bg-emerald-500/10 text-emerald-400" sub="Серверы, свитчи, патч-панели" to={`/${dcCode}/equipment`} />
            <KpiCard label="Кабельные линии" value={stats.total_cables} icon={Network} badgeColor="bg-amber-500/10 text-amber-400" sub="Проложенные коммутации" to={`/${dcCode}/cables`} />
            <KpiCard
              label="Утилизация юнитов"
              value={`${stats.space_utilization_pct.toFixed(1)}%`}
              sub={`${stats.used_u_slots}U занято / ${stats.free_u_slots}U свободно`}
            >
              <ProgressRing
                value={stats.space_utilization_pct}
                colorClass={
                  stats.space_utilization_pct > 80
                    ? 'text-status-critical'
                    : stats.space_utilization_pct > 60
                    ? 'text-status-warning'
                    : 'text-status-normal'
                }
              />
            </KpiCard>
          </>
        )}
      </div>

      {/* Spatial occupancy map & Power statistics split layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Interactive room map */}
        <div className="lg:col-span-2 bg-slate-900/40 backdrop-blur-md border border-slate-800/50 p-6 rounded-sm flex flex-col justify-between space-y-4 shadow-sm">
          <div className="flex items-center justify-between border-b border-slate-800/30 pb-3">
            <div>
              <h3 className="text-sm font-semibold text-text-primary">План заполнения зала</h3>
              <p className="text-xs text-text-muted mt-0.5">Интерактивный срез размещения стоек по рядам</p>
            </div>
            <div className="flex items-center gap-4">
              {dcCode && (
                <Link
                  to={`/${dcCode}/floor-plan`}
                  className="flex items-center gap-1.5 text-xs text-accent hover:text-accent-hover font-semibold transition-colors"
                  title="Открыть полноэкранный план зала"
                >
                  <LayoutGrid className="w-3.5 h-3.5" />
                  <span>План зала</span>
                </Link>
              )}
              <div className="flex items-center gap-3 text-[10px] uppercase font-bold text-text-secondary">
                <div className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-xs border map-cell-active" /> Активная</div>
                <div className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-xs border map-cell-blocked" /> Заблокирована</div>
                <div className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-xs border border-dashed map-cell-empty" /> Пустая плитка</div>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto py-2">
            <div className="grid gap-1.5" style={{ minWidth: `${cols * 28 + 60}px` }}>
              {Array.from({ length: rows }).map((_, r) => {
                const rowIndex = r
                return (
                  <div key={rowIndex} className="flex items-center gap-2">
                    <span className="w-12 text-right text-xs font-mono font-bold text-text-muted select-none pr-2">
                      Ряд {rowIndex + 1}
                    </span>
                    <div className="flex items-center gap-1">
                      {Array.from({ length: cols }).map((_, c) => {
                        const colIndex = c
                        const rack = racks.find(
                          item => item.row_index === rowIndex && item.rack_index === colIndex
                        )

                        if (!rack) {
                          return (
                            <div
                              key={colIndex}
                              className="w-7 h-7 rounded-sm border border-dashed map-cell-empty flex items-center justify-center text-[9px] font-mono select-none"
                              title={`Ряд ${rowIndex + 1}, Место ${colIndex + 1} (Свободно)`}
                            >
                              +
                            </div>
                          )
                        }

                        const isBlocked = rack.is_blocked
                        const display = rackLabel({
                          label: rack.label,
                          row_index: rack.row_index,
                          rack_index: rack.rack_index,
                        })
                        const short = display.includes('/')
                          ? String(rack.rack_index + 1)
                          : (display.length > 5 ? String(rack.rack_index + 1) : display)
                        const rackTitle = `${display}\nМесто: ${colIndex + 1}\nСлотов: ${rack.u_slots || 42}U\nЛимиты: ${rack.max_power_w || 0}Вт / ${rack.max_weight_kg || 0}кг`

                        return (
                          <Link
                            key={colIndex}
                            to={`/${dcCode}/racks/${rack.id}`}
                            className={`w-7 h-7 rounded-sm flex items-center justify-center text-[10px] font-mono font-bold transition-all border ${
                              isBlocked
                                ? 'map-cell-blocked hover:shadow-[0_0_8px_rgba(239,68,68,0.25)] hover:border-status-critical/60'
                                : 'map-cell-active hover:shadow-[0_0_8px_rgba(59,130,246,0.25)] hover:border-accent/60'
                            } hover:scale-105 cursor-pointer`}
                            title={rackTitle}
                          >
                            {short}
                          </Link>
                        )
                      })}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-text-muted bg-slate-900/20 border border-slate-800/30 p-2.5 rounded-sm">
            <Info className="w-4 h-4 text-accent shrink-0" />
            <span>Подпись ячейки — позиция в ряду; полное имя стойки (label) — во всплывающей подсказке. Нажмите для паспорта.</span>
          </div>
        </div>

        {/* Resource balancing details */}
        <div className="bg-slate-900/40 backdrop-blur-md border border-slate-800/50 p-6 rounded-sm flex flex-col justify-between shadow-sm">
          <div className="flex items-center justify-between border-b border-slate-800/30 pb-3">
            <h3 className="text-sm font-semibold text-text-primary">Распределение питания</h3>
            <p className="text-xs text-text-muted mt-0.5">Балансировка нагрузки PDU ввода A и ввода B</p>
          </div>

          {stats && (
            <div className="space-y-6 my-auto py-4">
              {/* PDU A */}
              <div className="space-y-2">
                <div className="flex justify-between text-xs font-semibold">
                  <span className="text-slate-200">Ввод питания A (PDU A)</span>
                  <span className="text-text-secondary font-mono">{stats.pdu_a_power_w} Вт</span>
                </div>
                <div className="w-full bg-slate-950 rounded-full h-3.5 overflow-hidden border border-slate-800/30">
                  <div
                    className="h-full bg-gradient-to-r from-emerald-500/50 to-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.15)] transition-all duration-700 ease-out"
                    style={{
                      width: `${
                        stats.total_power_w > 0
                          ? (stats.pdu_a_power_w / stats.total_power_w) * 100
                          : 50
                      }%`
                    }}
                  />
                </div>
              </div>

              {/* PDU B */}
              <div className="space-y-2">
                <div className="flex justify-between text-xs font-semibold">
                  <span className="text-slate-200">Ввод питания B (PDU B)</span>
                  <span className="text-text-secondary font-mono">{stats.pdu_b_power_w} Вт</span>
                </div>
                <div className="w-full bg-slate-950 rounded-full h-3.5 overflow-hidden border border-slate-800/30">
                  <div
                    className="h-full bg-gradient-to-r from-amber-500/50 to-amber-400 shadow-[0_0_8px_rgba(245,158,11,0.15)] transition-all duration-700 ease-out"
                    style={{
                      width: `${
                        stats.total_power_w > 0
                          ? (stats.pdu_b_power_w / stats.total_power_w) * 100
                          : 50
                      }%`
                    }}
                  />
                </div>
              </div>

              <div className="flex items-center justify-between text-xs border-t border-slate-800/30 pt-4 font-mono text-text-muted">
                <span>Общая потребляемая мощность:</span>
                <span className="text-slate-200 font-bold">{stats.total_power_w} Вт</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Grid: Quick start Actions & Service Monitors */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Quick Launchpad Workbench */}
        <div className="lg:col-span-2 bg-slate-900/40 backdrop-blur-md border border-slate-800/50 p-6 rounded-sm flex flex-col justify-between shadow-sm">
          <div className="flex items-center justify-between border-b border-slate-800/30 pb-3 mb-4">
            <div>
              <h3 className="text-sm font-semibold text-text-primary">Панель быстрого запуска</h3>
              <p className="text-xs text-text-muted mt-0.5">Ключевые модули и инструменты проектирования</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ActionBlock
              to={`/${dcCode}/floor-plan`}
              title="План зала ЦОД"
              desc="Визуальный интерактивный редактор компоновки машинного зала, размещения стоек и коридоров."
              icon={LayoutGrid}
              colorClass="text-blue-400"
            />
            <ActionBlock
              to={`/${dcCode}/connectivity`}
              title="Кабельный верстак"
              desc="Связывание портов оборудования, прокладка оптических/медных патч-кордов и магистралей."
              icon={Network}
              colorClass="text-amber-400"
            />
            <ActionBlock
              to={`/${dcCode}/equipment`}
              title="Каталог оборудования"
              desc="Список смонтированных серверов, свитчей и патч-панелей с учетом ограничений."
              icon={Server}
              colorClass="text-emerald-400"
            />
            <ActionBlock
              to={`/${dcCode}/power`}
              title="Анализатор питания"
              desc="Контроль нагрузки на вводы, фазовый баланс и распределение по шкафам питания."
              icon={Zap}
              colorClass="text-purple-400"
            />
          </div>
        </div>

        {/* Services & Integrations health monitor */}
        <div className="bg-slate-900/40 backdrop-blur-md border border-slate-800/50 p-6 rounded-sm flex flex-col justify-between shadow-sm">
          <div className="flex items-center justify-between border-b border-slate-800/30 pb-3 mb-4">
            <div>
              <h3 className="text-sm font-semibold text-text-primary">Статус платформы</h3>
              <p className="text-xs text-text-muted mt-0.5">Мониторинг работоспособности модулей</p>
            </div>
          </div>

          <div className="divide-y divide-slate-800/30">
            {services.map(s => (
              <ServiceStatus key={s.name} name={s.name} status={s.status} />
            ))}
          </div>

          <div className="mt-4 pt-3 border-t border-slate-800/30 flex items-center justify-between text-xs text-text-muted">
            <span className="flex items-center gap-1"><Settings className="w-3.5 h-3.5 text-text-muted" /> Версия API:</span>
            <span className="font-mono font-semibold text-text-secondary">{health?.version ?? '—'}</span>
          </div>
        </div>
      </div>
    </div>
  )
}
